/**
 * The root `/context` page (Context OS) was folded into the Data area on
 * 2026-10-01. Every old link keeps working: `/context`, any `/context/*`
 * path, and the old in-page anchors (`/context#connect-agents`, `#skills`,
 * `#activity`, …) land on the matching Data page and section. Query strings
 * carry over unchanged.
 *
 *   Context OS section                     → Data location
 *   hero, connect, walkthrough, examples,
 *   first-ten-minutes tour, quick guide     → /data/connect (Use with Claude)
 *   skills, skill publication, agents      → /data/skills
 *   coverage, Drive, activity, usage       → /data/you
 */
export const DATA_CONNECT_PATH = '/data/connect';
export const DATA_SKILLS_PATH = '/data/skills';
export const DATA_YOU_PATH = '/data/you';

/** Old `/context#<anchor>` → new location (path plus the new anchor). */
export const CONTEXT_HASH_TARGETS: Record<string, string> = {
  'context-os-main': DATA_CONNECT_PATH,
  'connect-agents': `${DATA_CONNECT_PATH}#connect`,
  'claude-walkthrough': `${DATA_CONNECT_PATH}#walkthrough`,
  'first-ten-minutes': `${DATA_CONNECT_PATH}#first-ten-minutes`,
  examples: `${DATA_CONNECT_PATH}#examples`,
  'one-connection-heading': `${DATA_CONNECT_PATH}#what-you-connect`,
  skills: DATA_SKILLS_PATH,
  agents: `${DATA_SKILLS_PATH}#agents`,
  activity: `${DATA_YOU_PATH}#activity`,
  'lake-truth-heading': `${DATA_YOU_PATH}#coverage`,
};

/** Old or guessed `/context/<segment>` paths → new location. */
export const CONTEXT_PATH_TARGETS: Record<string, string> = {
  '': DATA_CONNECT_PATH,
  connect: `${DATA_CONNECT_PATH}#connect`,
  setup: `${DATA_CONNECT_PATH}#connect`,
  token: `${DATA_CONNECT_PATH}#connect`,
  tokens: `${DATA_CONNECT_PATH}#connect`,
  'get-started': `${DATA_CONNECT_PATH}#first-ten-minutes`,
  walkthrough: `${DATA_CONNECT_PATH}#walkthrough`,
  examples: `${DATA_CONNECT_PATH}#examples`,
  tools: `${DATA_CONNECT_PATH}#tools`,
  skills: DATA_SKILLS_PATH,
  agents: `${DATA_SKILLS_PATH}#agents`,
  you: DATA_YOU_PATH,
  me: DATA_YOU_PATH,
  settings: DATA_YOU_PATH,
  access: `${DATA_YOU_PATH}#coverage`,
  coverage: `${DATA_YOU_PATH}#coverage`,
  usage: `${DATA_YOU_PATH}#usage`,
  activity: `${DATA_YOU_PATH}#activity`,
  drive: `${DATA_YOU_PATH}#drive`,
  saved: '/data/saved',
};

const withSearch = (target: string, search: string): string => {
  if (!search || search === '?') return target;
  const query = search.startsWith('?') ? search : `?${search}`;
  const hashIndex = target.indexOf('#');
  return hashIndex === -1
    ? `${target}${query}`
    : `${target.slice(0, hashIndex)}${query}${target.slice(hashIndex)}`;
};

/** Where an old `/context…` URL lives now. Unknown paths go to Use with Claude. */
export const contextRedirectTarget = (pathname: string, search = '', hash = ''): string => {
  const segment = pathname.replace(/^\/context\/?/, '').replace(/\/+$/, '').split('/')[0]?.toLowerCase() ?? '';
  const anchor = hash.replace(/^#\/?/, '');
  let target = CONTEXT_PATH_TARGETS[segment] ?? DATA_CONNECT_PATH;
  if (segment === '' && anchor) {
    target = CONTEXT_HASH_TARGETS[anchor] ?? `${DATA_CONNECT_PATH}#${anchor}`;
  }
  return withSearch(target, search);
};
